import React, { useEffect, useState } from 'react';
import { X, Key, Copy, RefreshCw, Trash2, Check, AlertTriangle, Eye, EyeOff, Webhook, Edit2 } from 'lucide-react';
import { supabase } from '../lib/db.ts';

interface PartnerApiKeysModalProps {
  partnerName: string;
  partnerId: string;
  onClose: () => void;
}

interface ApiKey {
  id: string;
  key_name: string;
  provider: string;
  is_active: boolean;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  created_by_email: string;
  webhook_url?: string | null;
  metadata?: Record<string, unknown>;
}

export function PartnerApiKeysModal({ partnerName, partnerId, onClose }: PartnerApiKeysModalProps) {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(true);
  const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [updatingWebhook, setUpdatingWebhook] = useState(false);

  const fetchApiKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the updated list_api_keys function that includes webhook_url
      const { data, error } = await supabase.rpc('list_api_keys', {
        p_provider: partnerName.toLowerCase().replace(/\s+/g, '')
      });
      if (error) throw error;
      
      // Filter keys for this specific partner
      const partnerKeys = (data || []).filter((key: ApiKey) => 
        key.metadata?.partner_id === partnerId
      );
      
      setApiKeys(partnerKeys);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to load API keys. Please ensure you have admin permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
    // eslint-disable-next-line
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setNewKey(null);
    try {
      // Generate key name with current date
      const keyName = `${partnerName} API Key ${new Date().toISOString().slice(0, 10)}`;
      // Convert partner name to provider format (lowercase, no spaces)
      const provider = partnerName.toLowerCase().replace(/\s+/g, '');
      
      const { data, error } = await supabase.rpc('generate_api_key', {
        p_key_name: keyName,
        p_provider: provider,
        p_expires_at: null, // No expiration by default
        p_metadata: {
          description: `API key for ${partnerName} partner integration`,
          partner_name: partnerName,
          partner_id: partnerId
        },
        p_created_for_user_id: partnerId
      });
      
      if (error) throw error;
      
      if (data && data.success) {
        setNewKey(data.api_key);
        setShowNewKey(true);
        fetchApiKeys(); // Refresh the list
      } else {
        throw new Error('Failed to generate API key');
      }
    } catch (err) {
      console.error('Error generating API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!globalThis.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    setRevokingId(id);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('revoke_api_key', { 
        p_api_key_id: id 
      });
      
      if (error) throw error;
      
      if (data && data.success) {
        fetchApiKeys(); // Refresh the list
      } else {
        throw new Error(data?.error || 'Failed to revoke API key');
      }
    } catch (err) {
      console.error('Error revoking API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopy = async (text: string, keyId?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (keyId) {
        setCopiedKeyId(keyId);
        setTimeout(() => setCopiedKeyId(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleEditWebhook = (apiKey: ApiKey) => {
    setEditingWebhookId(apiKey.id);
    setWebhookUrl(apiKey.webhook_url || '');
  };

  const handleCancelWebhookEdit = () => {
    setEditingWebhookId(null);
    setWebhookUrl('');
  };

  const handleUpdateWebhook = async (apiKeyId: string) => {
    if (!webhookUrl.trim()) {
      setError('Please enter a valid webhook URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(webhookUrl);
    } catch {
      setError('Please enter a valid HTTP or HTTPS URL');
      return;
    }

    setUpdatingWebhook(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('update_api_key_webhook', {
        p_api_key_id: apiKeyId,
        p_webhook_url: webhookUrl.trim()
      });

      if (error) throw error;

      if (data && data.success) {
        setEditingWebhookId(null);
        setWebhookUrl('');
        fetchApiKeys(); // Refresh the list
      } else {
        throw new Error('Failed to update webhook URL');
      }
    } catch (err) {
      console.error('Error updating webhook URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to update webhook URL');
    } finally {
      setUpdatingWebhook(false);
    }
  };

  const handleRemoveWebhook = async (apiKeyId: string) => {
    if (!globalThis.confirm('Are you sure you want to remove the webhook URL? You will no longer receive status notifications.')) {
      return;
    }

    setUpdatingWebhook(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('update_api_key_webhook', {
        p_api_key_id: apiKeyId,
        p_webhook_url: null
      });

      if (error) throw error;

      if (data && data.success) {
        fetchApiKeys(); // Refresh the list
      } else {
        throw new Error('Failed to remove webhook URL');
      }
    } catch (err) {
      console.error('Error removing webhook URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove webhook URL');
    } finally {
      setUpdatingWebhook(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Key className="w-5 h-5 text-wayja-teal" />
            API Keys for {partnerName}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* New API Key Display */}
        {newKey && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              New API Key Generated
            </h4>
            <p className="text-sm text-yellow-700 mb-3">
              ⚠️ This key will only be shown once. Please copy and store it securely.
            </p>
            <div className="flex items-center gap-2 p-3 bg-white border rounded">
              <code className="flex-1 font-mono text-sm">
                {showNewKey ? newKey : '•'.repeat(newKey.length)}
              </code>
              <button
                type="button"
                onClick={() => setShowNewKey(!showNewKey)}
                className="p-1 hover:bg-gray-100 rounded"
                title={showNewKey ? 'Hide' : 'Show'}
              >
                {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => handleCopy(newKey)}
                className="px-3 py-1 bg-wayja-teal text-white rounded hover:bg-wayja-dark-teal text-sm flex items-center gap-1"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
            <button
              type="button"
              onClick={() => setNewKey(null)}
              className="mt-2 text-sm text-gray-600 hover:text-gray-800"
            >
              ✕ Dismiss
            </button>
          </div>
        )}

        {/* Generate New Key Button */}
        <div className="mb-6">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2 bg-wayja-teal text-white rounded hover:bg-wayja-dark-teal disabled:opacity-50 flex items-center gap-2"
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Key className="w-4 h-4" />
                Generate New API Key
              </>
            )}
          </button>
        </div>

        {/* API Keys List */}
        <div className="space-y-4">
          <h4 className="font-semibold">Existing API Keys</h4>
          
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading API keys...
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No API keys found for this partner.
            </div>
          ) : (
            <div className="grid gap-4">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className={`border rounded-lg p-4 ${
                    !key.is_active || isExpired(key.expires_at)
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h5 className="font-medium">{key.key_name}</h5>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        !key.is_active
                          ? 'bg-red-100 text-red-800'
                          : isExpired(key.expires_at)
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {!key.is_active
                          ? 'Revoked'
                          : isExpired(key.expires_at)
                          ? 'Expired'
                          : 'Active'}
                      </span>
                    </div>
                    {key.is_active && !isExpired(key.expires_at) && (
                      <button
                        type="button"
                        onClick={() => handleRevoke(key.id)}
                        disabled={revokingId === key.id}
                        className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 disabled:opacity-50 text-sm flex items-center gap-1"
                      >
                        {revokingId === key.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                        Revoke
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Created:</span> {formatDate(key.created_at)}
                    </div>
                    <div>
                      <span className="font-medium">Last Used:</span> {formatDate(key.last_used_at)}
                    </div>
                    <div>
                      <span className="font-medium">Expires:</span> {key.expires_at ? formatDate(key.expires_at) : 'Never'}
                    </div>
                    <div>
                      <span className="font-medium">Created By:</span> {key.created_by_email || 'Unknown'}
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-sm text-gray-600">Key ID:</span>
                    <code className="text-xs bg-white px-2 py-1 rounded border font-mono">
                      {key.id}
                    </code>
                    <button
                      type="button"
                      onClick={() => handleCopy(key.id, key.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="Copy Key ID"
                    >
                      {copiedKeyId === key.id ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-400" />
                      )}
                    </button>
                  </div>

                  {/* Webhook URL Section */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Webhook className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">Webhook URL</span>
                      </div>
                      {key.is_active && !isExpired(key.expires_at) && (
                        <button
                          type="button"
                          onClick={() => handleEditWebhook(key)}
                          disabled={updatingWebhook}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                        >
                          <Edit2 className="w-3 h-3" />
                          {key.webhook_url ? 'Edit' : 'Add'}
                        </button>
                      )}
                    </div>
                    
                    {editingWebhookId === key.id ? (
                      <div className="space-y-2">
                        <input
                          type="url"
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://your-domain.com/api/webhook"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-wayja-teal focus:border-transparent"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleUpdateWebhook(key.id)}
                            disabled={updatingWebhook}
                            className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {updatingWebhook ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )}
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelWebhookEdit}
                            disabled={updatingWebhook}
                            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {key.webhook_url ? (
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-white px-2 py-1 rounded border font-mono text-gray-700 truncate">
                              {key.webhook_url}
                            </code>
                            <button
                              type="button"
                              onClick={() => handleCopy(key.webhook_url!, `webhook-${key.id}`)}
                              className="p-1 hover:bg-gray-200 rounded"
                              title="Copy Webhook URL"
                            >
                              {copiedKeyId === `webhook-${key.id}` ? (
                                <Check className="w-3 h-3 text-green-600" />
                              ) : (
                                <Copy className="w-3 h-3 text-gray-400" />
                              )}
                            </button>
                            {key.is_active && !isExpired(key.expires_at) && (
                              <button
                                type="button"
                                onClick={() => handleRemoveWebhook(key.id)}
                                disabled={updatingWebhook}
                                className="p-1 hover:bg-red-200 rounded text-red-600"
                                title="Remove Webhook URL"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 italic">
                            No webhook URL configured. Market status notifications will not be sent.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {key.webhook_url && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <strong>Webhook Events:</strong> You'll receive POST requests when market status changes 
                            (pending_approval → active, active → ended, etc.) for markets created with this API key.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h5 className="font-semibold text-blue-800 mb-2">Security Best Practices</h5>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• API keys are generated with the prefix 'tr_' and are 32 characters long</li>
            <li>• Keys are hashed using bcrypt before storage</li>
            <li>• Only active, non-expired keys are accepted for authentication</li>
            <li>• Usage is tracked with last_used_at timestamps</li>
            <li>• Revoked keys cannot be reactivated - generate new ones instead</li>
          </ul>
        </div>
      </div>
    </div>
  );
} 